import { isLocale, type Locale } from "../../shared/locales";

const WEB_ORIGIN = "https://metricspot.com";

export function marketingUrl(lang: string | Locale): string {
  if (!isLocale(lang) || lang === "en") return `${WEB_ORIGIN}/`;
  return `${WEB_ORIGIN}/${lang}/`;
}

/** URL-prefix the marketing site uses for a given lang. EN at "", others at "/<lang>". */
function marketingLangPrefix(lang: Locale): string {
  return lang === "en" ? "" : `/${lang}`;
}

/** Build a full URL to a marketing page in the given language. */
export function marketingPage(path: string, lang: string | Locale): string {
  const l: Locale = isLocale(lang) ? lang : "en";
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${WEB_ORIGIN}${marketingLangPrefix(l)}${p}`;
}

/** Marketing-site blog is en + es only (Astro Content Collection). */
export function marketingHasBlog(lang: string | Locale): boolean {
  return lang === "en" || lang === "es";
}

/** Header/footer chrome strings. Mirrors web/src/lib/i18n.ts entries used by Header.astro and Footer.astro. */
export interface MarketingChrome {
  nav_features: string;
  nav_pricing: string;
  nav_white_label: string;
  nav_blog: string;
  nav_signup: string;
  language_picker: string;
  footer_tagline: string;
  footer_product: string;
  footer_company: string;
  footer_terms: string;
  footer_privacy: string;
  footer_contact: string;
  footer_affiliates: string;
  footer_rights: string;
}

export const MARKETING_CHROME: Record<Locale, MarketingChrome> = {
  en: {
    nav_features: "Features",
    nav_pricing: "Pricing",
    nav_white_label: "White label",
    nav_blog: "Blog",
    nav_signup: "Start free",
    language_picker: "Language",
    footer_tagline: "SEO + AI-readability audits with white-label reports for agencies and freelancers.",
    footer_product: "Product",
    footer_company: "Company",
    footer_terms: "Terms",
    footer_privacy: "Privacy",
    footer_contact: "Contact",
    footer_affiliates: "Affiliates",
    footer_rights: "All rights reserved.",
  },
  es: {
    nav_features: "Funcionalidades",
    nav_pricing: "Precios",
    nav_white_label: "Marca blanca",
    nav_blog: "Blog",
    nav_signup: "Regístrate gratis",
    language_picker: "Idioma",
    footer_tagline: "Auditorías SEO + legibilidad IA con informes de marca blanca para agencias y freelancers.",
    footer_product: "Producto",
    footer_company: "Empresa",
    footer_terms: "Términos",
    footer_privacy: "Privacidad",
    footer_contact: "Contacto",
    footer_affiliates: "Afiliados",
    footer_rights: "Todos los derechos reservados.",
  },
  de: {
    nav_features: "Funktionen",
    nav_pricing: "Preise",
    nav_white_label: "White Label",
    nav_blog: "Blog",
    nav_signup: "Kostenlos starten",
    language_picker: "Sprache",
    footer_tagline: "SEO- und KI-Lesbarkeits-Audits mit White-Label-Berichten für Agenturen und Freelancer.",
    footer_product: "Produkt",
    footer_company: "Unternehmen",
    footer_terms: "AGB",
    footer_privacy: "Datenschutz",
    footer_contact: "Kontakt",
    footer_affiliates: "Partnerprogramm",
    footer_rights: "Alle Rechte vorbehalten.",
  },
  fr: {
    nav_features: "Fonctionnalités",
    nav_pricing: "Tarifs",
    nav_white_label: "Marque blanche",
    nav_blog: "Blog",
    nav_signup: "Commencer gratuitement",
    language_picker: "Langue",
    footer_tagline: "Audits SEO + lisibilité IA avec rapports en marque blanche pour agences et freelances.",
    footer_product: "Produit",
    footer_company: "Entreprise",
    footer_terms: "Conditions",
    footer_privacy: "Confidentialité",
    footer_contact: "Contact",
    footer_affiliates: "Affiliation",
    footer_rights: "Tous droits réservés.",
  },
  pt: {
    nav_features: "Funcionalidades",
    nav_pricing: "Preços",
    nav_white_label: "Marca branca",
    nav_blog: "Blog",
    nav_signup: "Começar grátis",
    language_picker: "Idioma",
    footer_tagline: "Auditorias SEO + legibilidade IA com relatórios em marca branca para agências e freelancers.",
    footer_product: "Produto",
    footer_company: "Empresa",
    footer_terms: "Termos",
    footer_privacy: "Privacidade",
    footer_contact: "Contacto",
    footer_affiliates: "Afiliados",
    footer_rights: "Todos os direitos reservados.",
  },
  it: {
    nav_features: "Funzionalità",
    nav_pricing: "Prezzi",
    nav_white_label: "White label",
    nav_blog: "Blog",
    nav_signup: "Inizia gratis",
    language_picker: "Lingua",
    footer_tagline: "Audit SEO + leggibilità IA con report white-label per agenzie e freelance.",
    footer_product: "Prodotto",
    footer_company: "Azienda",
    footer_terms: "Termini",
    footer_privacy: "Privacy",
    footer_contact: "Contatti",
    footer_affiliates: "Affiliati",
    footer_rights: "Tutti i diritti riservati.",
  },
  ca: {
    nav_features: "Funcionalitats",
    nav_pricing: "Preus",
    nav_white_label: "Marca blanca",
    nav_blog: "Blog",
    nav_signup: "Comença gratis",
    language_picker: "Idioma",
    footer_tagline: "Auditories SEO + llegibilitat IA amb informes de marca blanca per a agències i freelances.",
    footer_product: "Producte",
    footer_company: "Empresa",
    footer_terms: "Termes",
    footer_privacy: "Privadesa",
    footer_contact: "Contacte",
    footer_affiliates: "Afiliats",
    footer_rights: "Tots els drets reservats.",
  },
};
